/* fbp-capacity-gate.js
   ============================================================
   FBP Daily Capacity Gate (SOFT-only, Meta-safe, Monster-safe)
   - No slot marker item in cart
   - No fetch/XHR monkeypatching (Monster-safe)
   - Uses /cart.js + /variants/{slot}.js to compute remaining
   - Shows product banner when sold out (your existing CSS class)
   - Also refreshes cart-line notices via window.FBP_CartLineNotice (if present)

   Commission lines identified via line-item properties:
     { "_custom_commission": "1", "_slots_required": "N" }

   Requires (recommended):
     window.__FBP_IS_COMMISSION__ set on product pages
   ============================================================
*/
(function () {
  "use strict";

  console.log("[FBP] capacity-gate loaded vCLEAN");

  /* =========================
     CONFIG
  ========================= */
  var SLOT_VARIANT_ID = 43003896856665;

  var COMMISSION_FLAG_KEY = "_custom_commission";
  var SLOTS_REQUIRED_KEY = "_slots_required";

  var SOLD_OUT_LABEL = "Sold Out for Today";
  var SOLD_OUT_MESSAGE =
    "Daily custom artwork slots are limited. Final availability is confirmed at checkout.";

  var DOM_RECHECK_DEBOUNCE_MS = 350;
  var NETWORK_COOLDOWN_MS = 1500;
  var NETWORK_CACHE_TTL_MS = 1500;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  /* =========================
     POLICY
  ========================= */
  function getPolicy() {
    var p = String(window.__FBP_CAPACITY_POLICY__ || "SOFT").toUpperCase();
    return p === "HARD" ? "HARD" : "SOFT";
  }

  /* =========================
     INTERNAL STATE
  ========================= */
  var __fbpApplyingUI = false;
  var __fbpLastBannerState = { visible: null, message: null };
  var __fbpLastUiState = { soldOut: null, policy: null };

  /* =========================
     PAGE DETECTION
  ========================= */
  function isCommissionProductPage() {
    // GemPages/fast path
    if (typeof window.__FBP_IS_COMMISSION__ === "boolean") return window.__FBP_IS_COMMISSION__;

    // Fallback: your older method
    var productId = window && window.meta && window.meta.product && window.meta.product.id;
    if (!productId) return false;
    var txt = ($("#tpo-store-data") && $("#tpo-store-data").textContent) || "";
    return txt.indexOf(String(productId)) !== -1 && txt.indexOf("custom-commission") !== -1;
  }

  /* =========================
     SLOTS (product-page add attempt)
  ========================= */
  function getSlotsRequiredFromInputs() {
    var el = document.querySelector('[name="properties[_slots_required]"]');
    var n = el ? parseInt(el.value, 10) : NaN;
    return isFinite(n) && n > 0 ? n : 0;
  }

  function getQtyFromInputs() {
    var q = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
    var n = q ? parseInt(q.value, 10) : NaN;
    return isFinite(n) && n > 0 ? n : 1;
  }

  function inferSlotsRequiredFromDom() {
    var selects = Array.prototype.slice.call(
      document.querySelectorAll('select[name^="options["]')
    );
    var MIN = 2,
      MAX = 6;

    for (var i = 0; i < selects.length; i++) {
      var sel = selects[i];
      var opts = Array.prototype.slice
        .call(sel.options || [])
        .map(function (o) {
          return String(o.value || o.text || "").trim();
        });

      var ints = opts
        .map(function (v) {
          return /^\d+$/.test(v) ? parseInt(v, 10) : null;
        })
        .filter(function (n) {
          return isFinite(n);
        });

      if (ints.length < 2) continue;

      var min = Math.min.apply(null, ints);
      var max = Math.max.apply(null, ints);

      var looksLikeCount =
        min >= MIN &&
        max <= MAX &&
        ints.every(function (n) {
          return n >= MIN && n <= MAX;
        });

      if (!looksLikeCount) continue;

      var selected = String(sel.value || "").trim();
      if (/^\d+$/.test(selected)) return parseInt(selected, 10);

      return 1;
    }

    return 1;
  }

  function getThisAddSlots() {
    var per = getSlotsRequiredFromInputs() || inferSlotsRequiredFromDom() || 1;
    var qty = getQtyFromInputs();
    return per * qty;
  }

  /* =========================
     NETWORK (throttled/cached)
  ========================= */
  var lastNetworkCheckAt = 0;
  var lastNetworkResult = null; // { invQty, currentNeeded, checkedAt }
  var inflightNetworkPromise = null;

  function fetchCart() {
    return fetch("/cart.js", { cache: "no-store", credentials: "same-origin" }).then(function (r) {
      if (!r.ok) throw new Error("cart fetch failed");
      return r.json();
    });
  }

  function fetchSlotVariant() {
    return fetch("/variants/" + SLOT_VARIANT_ID + ".js", {
      cache: "no-store",
      credentials: "same-origin",
    }).then(function (r) {
      if (!r.ok) throw new Error("variant fetch failed");
      return r.json();
    });
  }

  function computeCommissionSlotsNeeded(cart) {
    var needed = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      var props = line.properties || {};
      if (String(props[COMMISSION_FLAG_KEY]) !== "1") continue;

      var per = parseInt(props[SLOTS_REQUIRED_KEY], 10);
      var perSlots = isFinite(per) && per > 0 ? per : 1;
      needed += (line.quantity || 0) * perSlots;
    }
    return needed;
  }

  function getNetworkStateThrottled(opts) {
    opts = opts || {};
    var force = !!opts.force;
    var now = Date.now();

    if (!force && lastNetworkResult && now - lastNetworkResult.checkedAt < NETWORK_CACHE_TTL_MS) {
      return Promise.resolve(lastNetworkResult);
    }
    if (!force && now - lastNetworkCheckAt < NETWORK_COOLDOWN_MS && lastNetworkResult) {
      return Promise.resolve(lastNetworkResult);
    }
    if (inflightNetworkPromise) return inflightNetworkPromise;

    lastNetworkCheckAt = now;

    inflightNetworkPromise = Promise.all([fetchCart(), fetchSlotVariant()]).then(function (arr) {
      var cart = arr[0];
      var variant = arr[1];

      var invQty =
        variant && typeof variant.inventory_quantity === "number"
          ? variant.inventory_quantity
          : null;

      var currentNeeded = computeCommissionSlotsNeeded(cart);
      var result = { invQty: invQty, currentNeeded: currentNeeded, checkedAt: Date.now() };
      lastNetworkResult = result;
      return result;
    });

    return inflightNetworkPromise.finally(function () {
      inflightNetworkPromise = null;
    });
  }

  function invalidateNetworkCache() {
    lastNetworkResult = null;
  }

   /* =========================
     PRODUCT PAGE BANNER (DISABLED)
     - You said you want GemPages to handle product-page messaging.
     - So we do NOT mount/move the banner under the ATC button anymore.
     - We leave the banner id alone so the CART banner can still exist.
  ========================= */

  var ENABLE_PRODUCT_BANNER = false;

  function isCartPage() {
    return /^\/cart(\/|$)/i.test(location.pathname || "");
  }

  // If you ever want to re-enable product banner later,
  // set ENABLE_PRODUCT_BANNER = true and this will mount it.
  function ensureProductBannerNode() {
    if (!ENABLE_PRODUCT_BANNER) return null;
    if (isCartPage()) return null; // never touch cart

    var el = document.getElementById("fbp-capacity-banner");

    if (!el) {
      el = document.createElement("div");
      el.id = "fbp-capacity-banner";
      el.className = "fbp-capacity-banner";
      el.setAttribute("role", "status");
    }

    // Find likely visible ATC button (GemPages + theme safe)
    var candidates = Array.prototype.slice.call(
      document.querySelectorAll(
        [
          "form[action*='/cart/add'] button[type='submit']",
          "form[action*='/cart/add'] [type='submit']",
          "button[name='add']",
          "[name='add'][type='submit']",
          ".product-form__submit",
          "button[type='submit'][data-add-to-cart]",
          "button[type='submit'][data-atc]",
          "button[type='submit'][data-action='add-to-cart']",
        ].join(",")
      )
    );

    var atcButton =
      candidates.find(function (btn) {
        if (!btn) return false;
        var r = btn.getBoundingClientRect && btn.getBoundingClientRect();
        return r && r.width > 0 && r.height > 0;
      }) ||
      candidates[0] ||
      null;

    if (atcButton && atcButton.insertAdjacentElement) {
      var next = atcButton.nextElementSibling;
      if (next !== el) {
        try { el.remove(); } catch (_) {}
        atcButton.insertAdjacentElement("afterend", el);
      }
    }

    return el;
  }

  function setProductBannerVisible(isVisible, message) {
    if (!ENABLE_PRODUCT_BANNER) return;
    if (isCartPage()) return;

    if (
      __fbpLastBannerState.visible === !!isVisible &&
      __fbpLastBannerState.message === (message || "")
    ) return;

    __fbpLastBannerState = { visible: !!isVisible, message: message || "" };

    var el = ensureProductBannerNode();
    if (!el) return;

    el.textContent = message || "";
    el.style.display = isVisible ? "block" : "none";
  }

  
/* =========================
   CART BANNER: STICKY MOUNT (EMPTY-CART SAFE)
   - Only runs on /cart
   - Mounts banner into a stable container that exists even when cart is empty
   - Re-mounts if Monster/theme rerenders and removes it
   - Forces message + visible on /cart (independent of "sold out" verdict)
========================= */
(function initCartBannerStickyMount() {
  var isCart = /^\/cart(\/|$)/i.test(location.pathname || "");
  if (!isCart) return;

  var MESSAGE = SOLD_OUT_MESSAGE; // reuse your existing message string

  function getOrCreateBannerEl() {
    var el = document.getElementById("fbp-capacity-banner");
    if (!el) {
      el = document.createElement("div");
      el.id = "fbp-capacity-banner";
      el.className = "fbp-capacity-banner";
      el.setAttribute("role", "status");
    }
    return el;
  }

  // IMPORTANT: choose anchors that exist on empty cart too
  function findStableCartAnchor() {
    return (
      document.querySelector("#MainContent") ||
      document.querySelector("main") ||
      document.querySelector(".main-content") ||
      document.querySelector("[role='main']") ||
      document.body
    );
  }

  // If there is a cart header/title when non-empty, we prefer to place after it.
  // But if empty cart template is different, we still insert near top of main.
  function findNicePlacementAnchor() {
    return (
      document.querySelector(".cart__header") ||
      document.querySelector(".cart-header") ||
      document.querySelector("h1.cart__title") ||
      document.querySelector("[data-cart-title]") ||
      document.querySelector("[data-cart-header]") ||
      null
    );
  }

  function forceVisibleCartBanner(el) {
    // Always show on /cart, even when empty
    el.textContent = MESSAGE || "";
    el.style.display = "block";
    el.style.visibility = "visible";
    el.style.opacity = "1";
  }

  function mountCartBanner() {
    var el = getOrCreateBannerEl();
    if (!el) return;

    // If it's already in the DOM, keep it visible and exit
    if (el.isConnected) {
      forceVisibleCartBanner(el);
      return;
    }

    var nice = findNicePlacementAnchor();
    var stable = findStableCartAnchor();
    if (!stable) return;

    // Insert after header if it exists, otherwise at top of stable container
    try {
      if (nice && nice.insertAdjacentElement) {
        nice.insertAdjacentElement("afterend", el);
      } else if (stable.insertAdjacentElement) {
        stable.insertAdjacentElement("afterbegin", el);
      } else if (stable.prepend) {
        stable.prepend(el);
      } else {
        stable.appendChild(el);
      }
    } catch (_) {}

    forceVisibleCartBanner(el);
  }

  // Run now + after Monster renders
  mountCartBanner();
  setTimeout(mountCartBanner, 50);
  setTimeout(mountCartBanner, 200);
  setTimeout(mountCartBanner, 800);
  setTimeout(mountCartBanner, 1600);

  // Debounced observer to remount if removed
  var t = null;
  try {
    var mo = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () {
        mountCartBanner();
      }, 150);
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (_) {}
})();



  /* =========================
     VERDICT
  ========================= */
  function capacityVerdict(opts) {
    opts = opts || {};
    var forceNetwork = !!opts.forceNetwork;

    return getNetworkStateThrottled({ force: forceNetwork }).then(function (state) {
      if (!state || state.invQty == null) {
        return { ok: true, state: state, remaining: null, thisAddSlots: null };
      }

      var currentNeeded = isFinite(state.currentNeeded) ? state.currentNeeded : 0;
      var remaining = state.invQty - currentNeeded;

      var thisAddSlots = getThisAddSlots();
      var ok = thisAddSlots <= remaining;

      return { ok: ok, state: state, remaining: remaining, thisAddSlots: thisAddSlots };
    });
  }



  function setCartBannerAlways(message) {
  if (!/^\/cart(\/|$)/i.test(location.pathname || "")) return;

  var el = document.getElementById("fbp-capacity-banner");
  if (!el) return;

  el.textContent = message || "";
  el.style.display = "block";
  el.style.visibility = "visible";
  el.style.opacity = "1";
}

function forceCartBanner(message) {
  if (!/^\/cart(\/|$)/i.test(location.pathname || "")) return;

  var el = document.getElementById("fbp-capacity-banner");
  if (!el) return;

  el.textContent = message || "";
  el.style.display = "block";
  el.style.visibility = "visible";
  el.style.opacity = "1";
}

  /* =========================
     UI UPDATE
  ========================= */
  function updateUI(soldOut) {
    var policy = getPolicy();
    var desired = { soldOut: !!soldOut, policy: policy };
    setCartBannerAlways(SOLD_OUT_MESSAGE);
    forceCartBanner(SOLD_OUT_MESSAGE);



    if (__fbpLastUiState.soldOut === desired.soldOut && __fbpLastUiState.policy === desired.policy) {
      if (ENABLE_PRODUCT_BANNER) {
      // ✅ CART: Always show the banner message (cart-only)
      if (/^\/cart(\/|$)/i.test(location.pathname || "")) {
        var cartEl = document.getElementById("fbp-capacity-banner");
        if (cartEl) {
          cartEl.textContent = SOLD_OUT_MESSAGE;
          cartEl.style.display = "block"; // force visible on cart
          cartEl.style.visibility = "visible";
          cartEl.style.opacity = "1";
        }
      }

      // Product banner disabled (GemPages handles it)
      setProductBannerVisible(desired.soldOut, SOLD_OUT_MESSAGE);
;
}
      if (window.FBP_CartLineNotice && typeof window.FBP_CartLineNotice.refresh === "function") {
        window.FBP_CartLineNotice.refresh();
      }
      return;
    }

    __fbpLastUiState = desired;

    __fbpApplyingUI = true;
    try {
      document.documentElement.classList.toggle("fbp-capacity-warning", desired.soldOut);

      setProductBannerVisible(desired.soldOut, SOLD_OUT_MESSAGE);

      // If cart-line notice script exists, refresh it (it decides commission-only)
      if (window.FBP_CartLineNotice && typeof window.FBP_CartLineNotice.refresh === "function") {
        window.FBP_CartLineNotice.refresh();
      }

      if (policy !== "HARD") return;

      // HARD is not recommended, but keep behavior if you flip it on:
      var candidates = Array.prototype.slice.call(
        document.querySelectorAll(
          [
            'form[action*="/cart/add"] button[type="submit"]',
            'form[action*="/cart/add"] [type="submit"]',
            'button[name="add"]',
            '[name="add"][type="submit"]',
            ".product-form__submit",
            'button[type="submit"][data-add-to-cart]',
            'button[type="submit"][data-atc]',
            'button[type="submit"][data-action="add-to-cart"]',
          ].join(",")
        )
      );

      for (var i = 0; i < candidates.length; i++) {
        var btn = candidates[i];
        if (!btn) continue;

        if (!btn.__fbp_original) {
          btn.__fbp_original = {
            text: btn.textContent,
            disabled: btn.disabled,
            ariaDisabled: btn.getAttribute("aria-disabled"),
          };
        }

        if (desired.soldOut) {
          btn.textContent = SOLD_OUT_LABEL;
          btn.disabled = true;
          btn.setAttribute("aria-disabled", "true");
          btn.classList.add("fbp-sold-out");
          btn.style.setProperty("pointer-events", "none", "important");
          btn.style.setProperty("opacity", "0.7", "important");
        } else {
          btn.textContent = btn.__fbp_original.text || "Add to cart";
          btn.disabled = !!btn.__fbp_original.disabled;

          if (btn.__fbp_original.ariaDisabled == null) btn.removeAttribute("aria-disabled");
          else btn.setAttribute("aria-disabled", btn.__fbp_original.ariaDisabled);

          btn.classList.remove("fbp-sold-out");
          btn.style.removeProperty("pointer-events");
          btn.style.removeProperty("opacity");
        }
      }
    } finally {
      __fbpApplyingUI = false;
    }
  }

  /* =========================
     RECHECK SCHEDULING
  ========================= */
  var domDebounceTimer = null;

  function scheduleRecheck(opts) {
    opts = opts || {};
    var forceNetwork = !!opts.forceNetwork;

    clearTimeout(domDebounceTimer);
    domDebounceTimer = setTimeout(function () {
      capacityVerdict({ forceNetwork: forceNetwork })
        .then(function (v) {
          updateUI(!v.ok);
        })
        .catch(function () {});
    }, DOM_RECHECK_DEBOUNCE_MS);
  }

  function recheckSoon(opts) {
    opts = opts || {};
    var forceNetwork = "forceNetwork" in opts ? !!opts.forceNetwork : true;
    invalidateNetworkCache();
    scheduleRecheck({ forceNetwork: forceNetwork });
  }

  /* =========================
     HOOKS
  ========================= */
  function hookATC() {
    var form = $("form[action*='/cart/add']");
    if (!form || form.__fbp_capacity_hooked) return;
    form.__fbp_capacity_hooked = true;

    // SOFT: do not block submit; just recheck
    form.addEventListener(
      "submit",
      function () {
        if (!isCommissionProductPage()) return;
        recheckSoon({ forceNetwork: true });
      },
      true
    );
  }

  function hookCartQtyUiChanges() {
    if (document.__fbp_cart_qty_hooked) return;
    document.__fbp_cart_qty_hooked = true;

    // If anything in the DOM changes significantly, refresh soldout state.
    // (This catches Monster rerenders after qty changes.)
    var moTimer = null;
    new MutationObserver(function () {
      if (__fbpApplyingUI) return;
      clearTimeout(moTimer);
      moTimer = setTimeout(function () {
        recheckSoon({ forceNetwork: false });
      }, 300);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* =========================
     INIT
  ========================= */
  function init() {
    hookCartQtyUiChanges();
    recheckSoon({ forceNetwork: true });
    if (isCommissionProductPage()) hookATC();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  window.FBP_CapacityGate = {
    init: init,
    getPolicy: getPolicy,
    capacityVerdict: capacityVerdict,
    _debug: { getNetworkStateThrottled: getNetworkStateThrottled },
  };
})();
